/* eslint-disable react-hooks/exhaustive-deps */
// ...existing code...
import { useEffect, useState } from "react";
import { getUserTemplate } from "../services/sellerNetService";

export const useUserTemplate = (
  mainId,
  userId,
  companyId,
  fieldArrayHandlers = {}
) => {
  const [templateMissing] = useState(false);

  useEffect(() => {
    // Populate RHF field arrays and nodes from the user's saved template.
    // This hook no longer depends on legacy `formData` or `setFormData`.

    // If the caller indicates we're editing an existing calc (calcId present)
    // or the parent is currently populating the form from server data,
    // do not apply the user's template. The parent passes `calcId` and
    // `isPopulatingRef` via `fieldArrayHandlers`.
    try {
      if (fieldArrayHandlers && fieldArrayHandlers.calcId) return;
      if (
        fieldArrayHandlers &&
        fieldArrayHandlers.isPopulatingRef &&
        fieldArrayHandlers.isPopulatingRef.current
      )
        return;
    } catch (e) {
      // ignore and continue
    }

    const toCamel = (s = "") =>
      s
        .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
        .replace(/^([A-Z])/, (m) => m.toLowerCase());

    const loadTemplate = async () => {
      if (!mainId || !userId || !companyId) return;

      try {
        const data = await getUserTemplate(companyId);
        //console.log('useUserTemplate: loaded template data', data);

        /* setTemplateMissing(false);
        if (Array.isArray(data) || typeof data === "object" && Object.keys(data).length === 0) {
          try { setTemplateMissing(true); } catch (e) { }
          //return;
        } */

        // Ensure we have a parsed `section1` array to build a lookup map.
        let section1 = data?.seller_closing_costs_section1 ?? null;

        if (typeof section1 === "string") {
          try {
            section1 = JSON.parse(section1);
          } catch (e) {
            /* keep original */
          }
        }

        if (!Array.isArray(section1) || section1.length === 0) return;

        const normalize = (k) =>
          String(k || "")
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");
        const map = new Map();
        for (const it of section1) {
          if (!it || !it.key) continue;
          map.set(normalize(it.key), it);
        }
        const get = (k) => map.get(normalize(k));

        const parseTemplateValue = (item) => {
          if (!item) return "0.00";
          let v = item?.value ?? item?.rate ?? item?.Cash ?? null;
          if (v == null) return "0.00";
          if (typeof v === "string") {
            const s = v.trim().replace(/,/g, "");
            if (s === "") return "0.00";
            return s;
            //  const n = Number(s);
            //   return Number.isFinite(n) ? n : s;
          }
          return v;
        };
        /*
                const sellerItem = get("seller_broker_comp_pct");
                const buyerItem = get("buyer_broker_comp_pct");
        
                const sellerLabel = sellerItem?.label ?? "";
                const buyerLabel = buyerItem?.label ?? "";
                const sellerRate = sellerItem?.rate ?? null;
                const buyerRate = buyerItem?.rate ?? null;
        */
        const transferTax = parseTemplateValue(get("transfer_tax_rate_per"));

        // parse section2 and map into ManualSheetSection fields (preserve UI mapping)
        let section2 = data?.seller_closing_costs_section2 ?? null;
        if (typeof section2 === "string") {
          try {
            section2 = JSON.parse(section2);
          } catch (e) {
            /* keep original */
          }
        }
        const parsedSection2 = [];
        // section2 generic values keyed by camelKey (do NOT inject these into root formData)
        const section2Extras = {};
        if (Array.isArray(section2)) {
          for (const it of section2) {
            if (!it || !it.key) continue;
            const value = parseTemplateValue(it);
            const camel = toCamel(it.key);
            parsedSection2.push({ ...it, value, camelKey: camel });

            // collect generic camel-keyed values but DO NOT expose them on root
            if (value != null) section2Extras[camel] = value;
          }
          // No automatic mapping of section2 items into root formData anymore.
          // Keep parsedSection2 as the authoritative array under closingCostMeta.
          // If the caller provided a fieldArray handler, populate the RHF field
          // array so form inputs backed by useFieldArray can show the values.
          if (
            parsedSection2.length &&
            typeof fieldArrayHandlers.replaceClosingCostsSection2 === "function"
          ) {
            try {
              fieldArrayHandlers.replaceClosingCostsSection2(parsedSection2);
            } catch (e) {
              console.warn(
                "useUserTemplate: failed to replace closingCostMeta field array",
                e
              );
            }
          }
        }

        // parse section1 into sellerClosingCostMeta array so ManualSheetSection can render it dynamically
        let section1Array = data?.seller_closing_costs_section1 ?? null;
        if (typeof section1Array === "string") {
          try {
            section1Array = JSON.parse(section1Array);
          } catch (e) {
            /* keep original */
          }
        }
        const parsedSection1 = [];
        if (Array.isArray(section1Array)) {
          const toCamel = (s = "") =>
            s
              .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
              .replace(/^([A-Z])/, (m) => m.toLowerCase());

          const IGNORE_KEYS = [
            "reconveyance_fee",
            "payoff_demand_statement",
            "prepayment_penality",
          ];
          for (const it of section1Array) {
            if (!it || !it.key) continue;
            if (IGNORE_KEYS.includes((it.key || "").toString())) continue;
            const value = parseTemplateValue(it);
            const camel = toCamel(it.key);
            //parsedSection1.push({ ...it, value, camelKey: camel });
            const key = (it.key || "").toString();
            if (key === "transfer_tax") {
              parsedSection1.push({ ...it, value, camelKey: camel, amount: 0 });
            } else {
              parsedSection1.push({ ...it, value, camelKey: camel });
            }
          }
        }

        // Populate sellerClosingCostMeta useFieldArray if handler provided
        if (
          parsedSection1.length &&
          typeof fieldArrayHandlers.replaceSellerClosingCost === "function"
        ) {
          try {
            fieldArrayHandlers.replaceSellerClosingCost(parsedSection1);
          } catch (e) {
            console.warn(
              "useUserTemplate: failed to replace sellerClosingCostMeta field array",
              e
            );
          }
        }
        // parse sellerOtherCosts and map into ManualSheetSection fields (preserve UI mapping)
        let sellerOtherCosts = data?.conventional_seller_closing_costs_section ?? null;
        const parsedSellerOtherCosts = [];
        if (Array.isArray(sellerOtherCosts)) {
          const toCamel = (s = "") =>
            s
              .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
              .replace(/^([A-Z])/, (m) => m.toLowerCase());
          // Keys to ignore when populating sellerOtherCostMeta
          const IGNORE_KEYS = new Set([
            "points_pct",
            "origination_factor_pct",
            "origination_factor",
          ]);
          for (const it of sellerOtherCosts) {
            if (!it || !it.key) continue;
            // Skip ignored keys
            if (IGNORE_KEYS.has((it.key || "").toString())) continue;
            const value = parseTemplateValue(it);
            const camel = toCamel(it.key);
            parsedSellerOtherCosts.push({ ...it, value, camelKey: camel });
          }
        }

        // Also collect other loan-type specific seller-other arrays (fha/usda/va)
        const parseOtherCostsArray = (arr) => {
          const out = [];
          if (!Array.isArray(arr)) return out;
          const toCamel = (s = "") =>
            s
              .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
              .replace(/^([A-Z])/, (m) => m.toLowerCase());
          const IGNORE_KEYS_LOCAL = new Set([
            "points_pct",
            "origination_factor_pct",
            "origination_factor",
          ]);
          for (const it of arr) {
            if (!it || !it.key) continue;
            if (IGNORE_KEYS_LOCAL.has((it.key || "").toString())) continue;
            let value = parseTemplateValue(it);
            // If the template item indicates a payor and it's not the Seller,
            // then zero out the value so seller-specific fields don't carry over.
            try {
              // look for any key that includes 'payor' (case-insensitive)
              const payorKey = Object.keys(it).find((k) =>
                (k || "").toString().toLowerCase().includes("payer")
              );
              if (payorKey) {
                const payorVal = (it[payorKey] ?? "")
                  .toString()
                  .trim()
                  .toLowerCase();
                if (payorVal && payorVal !== "seller") {
                  value = "0.00";
                }
              }
            } catch (e) {
              // ignore and keep original value
            }
            const camel = toCamel(it.key);
            out.push({ ...it, value, camelKey: camel });
          }
          return out;
        };
        const fhaOther = parseOtherCostsArray(
          data?.fha_closing_costs ?? data?.fha_closing_costs_section ?? []
        );
        const usdaOther = parseOtherCostsArray(
          data?.usda_closing_costs ?? data?.usda_closing_costs_section ?? []
        );
        const vaOther = parseOtherCostsArray(
          data?.va_closing_costs ?? data?.va_closing_costs_section ?? []
        );

        // Additionally pull corrective_work and buyers_fees from data?.seller_othercosts?.other_costs
        // into sellerOtherCostMeta (but do NOT include these in otherCostMeta).
        try {
          let extraOtherCosts = data?.seller_othercosts?.other_costs ?? null;
          if (typeof extraOtherCosts === "string") {
            try {
              extraOtherCosts = JSON.parse(extraOtherCosts);
            } catch (e) {
              /* keep original */
            }
          }
          if (Array.isArray(extraOtherCosts)) {
            const EXTRA_KEYS = new Set(["corrective_work", "buyers_fees"]);
            for (const it of extraOtherCosts) {
              if (!it || !it.key) continue;
              const keyNorm = (it.key || "").toString();
              if (!EXTRA_KEYS.has(keyNorm)) continue;
              // Avoid duplicates: only add if not already present by key
              const already = parsedSellerOtherCosts.find(
                (x) => (x.key || "").toString() === keyNorm
              );
              if (already) continue;
              const value = parseTemplateValue(it);
              const camel = toCamel(it.key);
              // Provide sensible defaults if the template item is missing fields
              const defaultShape = {
                unit: it.unit ?? "$",
                label:
                  it.label ??
                  (it.key === "corrective_work"
                    ? "Corrective Work"
                    : it.key === "buyers_fees"
                    ? "Buyer's Fees"
                    : it.key),
                sr_no:
                  it.sr_no ??
                  (it.key === "corrective_work"
                    ? 7
                    : it.key === "buyers_fees"
                    ? 8
                    : undefined),
              };
              parsedSellerOtherCosts.push({
                ...defaultShape,
                ...it,
                value,
                camelKey: camel,
              });
            }
          }
        } catch (e) {
          // non-critical: continue
        }
        // Populate sellerOtherCostMeta useFieldArray if handler provided
        if (
          parsedSellerOtherCosts.length &&
          typeof fieldArrayHandlers.replaceSellerOtherCost === "function"
        ) {
          try {
            fieldArrayHandlers.replaceSellerOtherCost(parsedSellerOtherCosts);
          } catch (e) {
            console.warn(
              "useUserTemplate: failed to replace sellerOtherCostMeta field array",
              e
            );
          }
        }

        // Also expose loan-type variants so the UI can switch sellerOtherCostMeta on buyersLoanType change
        const templateSellerOtherCosts = {
          conventional: parsedSellerOtherCosts,
          fha: fhaOther,
          usda: usdaOther,
          va: vaOther,
        };

        let otherCostMeta = data?.seller_othercosts?.other_costs ?? null;

        if (typeof otherCostMeta === "string") {
          try {
            otherCostMeta = JSON.parse(otherCostMeta);
          } catch (e) {
            /* keep original */
          }
        }
        const parsedOtherCosts = [];
        if (Array.isArray(otherCostMeta)) {
          const SKIP_KEYS = new Set(["corrective_work", "buyers_fees"]);
          for (const it of otherCostMeta) {
            if (!it || !it.key) continue;
            // Skip keys that should be moved into sellerOtherCostMeta
            if (SKIP_KEYS.has((it.key || "").toString())) continue;
            const value = parseTemplateValue(it);
            const camel = toCamel(it.key);
            const amount = parseTemplateValue(it);
            parsedOtherCosts.push({ ...it, value, camelKey: camel, amount });
          }
        }

        // Populate otherCostMeta useFieldArray if handler provided
        if (
          parsedOtherCosts.length &&
          typeof fieldArrayHandlers.replaceOtherCost === "function"
        ) {
          try {
            fieldArrayHandlers.replaceOtherCost(parsedOtherCosts);
          } catch (e) {
            console.warn(
              "useUserTemplate: failed to replace otherCostMeta field array",
              e
            );
          }
        }

        let compensationMeta =
          data?.seller_othercosts?.payments_to_buyer ?? null;

        if (typeof compensationMeta === "string") {
          try {
            compensationMeta = JSON.parse(compensationMeta);
          } catch (e) {
            /* keep original */
          }
        }
        const parsedPaymentToBuyer = [];
        if (Array.isArray(compensationMeta)) {
          const SKIP_PAYMENT_KEYS = new Set(["days_interest"]);
          for (const it of compensationMeta) {
            if (!it || !it.key) continue;
            // skip keys that are no longer required (days_interest should not be added)
            if (SKIP_PAYMENT_KEYS.has((it.key || "").toString())) continue;
            const value = parseTemplateValue(it);
            const camel = toCamel(it.key);
            parsedPaymentToBuyer.push({ ...it, value, camelKey: camel });
          }
        }

        // Populate compensationMeta useFieldArray if handler provided
        if (
          parsedPaymentToBuyer.length &&
          typeof fieldArrayHandlers.replaceCompensation === "function"
        ) {
          try {
            fieldArrayHandlers.replaceCompensation(parsedPaymentToBuyer);
          } catch (e) {
            console.warn(
              "useUserTemplate: failed to replace compensationMeta field array",
              e
            );
          }
        }

        // parse loanInfoMeta from template (support multiple shapes)
        let loanInfoMeta = null;
        loanInfoMeta = {
          annualPropertyTax: "0.00",
          assessorsTaxRate: "0.00",
          autoEstimateLoan: false,
          buyersLoanType: "Conventional",
          buyerDownPayment: "0.00",
          estimatedTaxProrations: "0.00",
          loansToBePaidBalance1: "0.00",
          loansToBePaidRate1: "0.00",
          loansToBePaidBalance2: "0.00",
          loansToBePaidRate2: "0.00",
          loansToBePaidBalance3: "0.00",
          loansToBePaidRate3: "0.00",
          transferTaxRatePer: transferTax,
          homeWarrantyAmount: "0.00",
          estimatedClosingDate: "",
          mortgageAmount: "0.00",
          conventionalPercent: "80.00",
          conventionalPercent2: "0.00",
          interestRate1: "0.00",
          interestRate2: "0.00",
          termsInYears1: "0",
          termsInYears2: "0",
          amount: "0.00",
          amount2: "0.00",
          adjusted: "0.00",
          noPMICheck: false,
          showAPRCheck: false,
          allowOwnerCarry1Check: false,
          allowOwnerCarry2Check: false,
          addDueYear1: "0.00",
          addDueYear2: "0.00",
        };

        let addressMeta = {
          address: "",
          unitNumber: "",
          street_no: "",
          street_name: "",
          city: "",
          state: "",
          county: "",
          zip: "",
          closing_state: "",
          closing_county: "",
          closing_zip_code: "",
        };

        let totalClosingMeta = {};
        totalClosingMeta = {
          totalAllCostsAndCredits_total: "0.00",
          totalAllPaymentToBuyer_total: "0.00",
          totalClosingCosts_total: "0.00",
          totalEstimatedCashToSeller: "0.00",
        };

        let titleSettlementMeta = {
          ownersTitleAmount: "0.00",
          ownersTitleSeller: "0.00",
          ownersTitlePayor: "Seller",

          escrowSettlementPayor: "Seller",
          escrowSettlementAmount: "0.00",
          escrowSettlementSeller: "0.00",
          escrowSettlementSplit: "0.00",

          lendersTitleAmount: "0.00",
          lendersTitleSeller: "0.00",
          lendersTitlePayor: "Seller",
        };

        // If caller provided an RHF setValue, populate RHF nodes directly.
        const setRHFValue =
          typeof fieldArrayHandlers.setValue === "function"
            ? fieldArrayHandlers.setValue
            : null;
        const getRHFValue =
          typeof fieldArrayHandlers.getValues === "function"
            ? fieldArrayHandlers.getValues
            : null;
        if (setRHFValue) {
          try {
            if (parsedSection2.length)
              setRHFValue("closingCostMeta", parsedSection2);
            if (parsedSection1.length)
              setRHFValue("sellerClosingCostMeta", parsedSection1);
            if (parsedSellerOtherCosts.length)
              setRHFValue("sellerOtherCostMeta", parsedSellerOtherCosts);
            if (titleSettlementMeta.length)
              setRHFValue("titleSettlementMeta", titleSettlementMeta);

            // Only set compensationMeta if caller hasn't already populated it
            try {
              const existingComp = getRHFValue
                ? getRHFValue("compensationMeta") ?? []
                : null;
              const hasExistingComp =
                Array.isArray(existingComp) && existingComp.length > 0;
              if (!hasExistingComp && parsedPaymentToBuyer.length) {
                setRHFValue("compensationMeta", parsedPaymentToBuyer);
              }
            } catch (e) {
              // fallback: attempt to set compensationMeta
              if (parsedPaymentToBuyer.length)
                try {
                  setRHFValue("compensationMeta", parsedPaymentToBuyer);
                } catch (err) {}
            }

            // Only set otherCostMeta if caller hasn't already populated it
            try {
              const existingOther = getRHFValue
                ? getRHFValue("otherCostMeta") ?? []
                : null;
              const hasExistingOther =
                Array.isArray(existingOther) && existingOther.length > 0;
              if (!hasExistingOther && parsedOtherCosts.length) {
                setRHFValue("otherCostMeta", parsedOtherCosts);
              }
            } catch (e) {
              // fallback: attempt to set otherCostMeta
              if (parsedOtherCosts.length)
                try {
                  setRHFValue("otherCostMeta", parsedOtherCosts);
                } catch (err) {
                  /* ignore */
                }
            }

            // Only set totalClosingMeta if caller hasn't already populated it
            try {
              const existingTotal = getRHFValue
                ? getRHFValue("totalClosingMeta") ?? {}
                : null;
              const hasExistingTotal =
                existingTotal &&
                Object.keys(existingTotal || {}).some((k) => {
                  const v = existingTotal[k];
                  return (
                    v !== null && v !== undefined && String(v).trim() !== ""
                  );
                });
              if (!hasExistingTotal && totalClosingMeta) {
                setRHFValue("totalClosingMeta", totalClosingMeta);
              }
            } catch (e) {
              // fallback: attempt to set totalClosingMeta
              try {
                if (totalClosingMeta)
                  setRHFValue("totalClosingMeta", totalClosingMeta);
              } catch (err) {
                /* ignore */
              }
            }
            // Only set addressMeta if the caller doesn't already have populated address data.
            try {
              const existingAddress = getRHFValue
                ? getRHFValue("addressMeta") ?? {}
                : null;
              const hasExistingAddress =
                existingAddress &&
                Object.keys(existingAddress || {}).some((k) => {
                  const v = existingAddress[k];
                  return (
                    v !== null && v !== undefined && String(v).trim() !== ""
                  );
                });
              if (!hasExistingAddress) {
                setRHFValue("addressMeta", addressMeta);
              }
            } catch (e) {
              // fallback: attempt to set addressMeta (don't crash)
              try {
                setRHFValue("addressMeta", addressMeta);
              } catch (err) {
                /* ignore */
              }
            }
            // Only set loanInfoMeta if the caller doesn't already have it populated
            try {
              const existingLoanInfo = getRHFValue
                ? getRHFValue("loanInfoMeta") ?? {}
                : null;
              const hasExistingLoanInfo =
                existingLoanInfo &&
                Object.keys(existingLoanInfo || {}).some((k) => {
                  const v = existingLoanInfo[k];
                  return (
                    v !== null && v !== undefined && String(v).trim() !== ""
                  );
                });
              if (!hasExistingLoanInfo) {
                setRHFValue("loanInfoMeta", loanInfoMeta);
              }
            } catch (e) {
              // fallback: attempt to set loanInfoMeta (don't crash)
              try {
                setRHFValue("loanInfoMeta", loanInfoMeta);
              } catch (err) {
                /* ignore */
              }
            }
            //check is salesPrice exists in template, if not set to 0.00
            const salesPrice = getRHFValue
              ? getRHFValue("salesPrice") ?? null
              : null;
            if (salesPrice == null) {
              setRHFValue("salesPrice", "0.00");
            }

            // expose parsed template variants for sellerOtherCostMeta so UI can swap on loan type
            try {
              setRHFValue("templateSellerOtherCosts", templateSellerOtherCosts);
            } catch (e) {
              // ignore if template variants not available
            }
            // seed a few simple root fields
            setRHFValue("preparedBy", "");
            setRHFValue("preparedFor", "New Client");
            setRHFValue("escrowPremiumChanged", false);
          } catch (e) {
            console.warn(
              "useUserTemplate: failed to set RHF values via setValue",
              e
            );
          }
        } else {
          // No RHF setValue provided: we can't populate the caller's state.
          // Log a warning so callers can opt-in to provide `setValue`.
          console.warn(
            "useUserTemplate: no RHF setValue provided — template not applied to form (provide fieldArrayHandlers.setValue)"
          );
        }
      } catch (err) {
        console.error("Error loading template:", err);
      }
    };

    loadTemplate();
  }, [
    mainId,
    userId,
    companyId,
    fieldArrayHandlers?.replaceSellerClosingCost,
    fieldArrayHandlers?.replaceClosingCostsSection2,
    fieldArrayHandlers?.replaceSellerOtherCost,
    fieldArrayHandlers?.replaceCompensation,
    fieldArrayHandlers?.replaceOtherCost,
    fieldArrayHandlers?.setValue,
    fieldArrayHandlers?.calcId,
    fieldArrayHandlers?.isPopulatingRef,
  ]);

  return { templateMissing };
};
// ...existing code...

// Expose an imperative loader so callers can load/apply the user template on-demand.
export const loadUserTemplate = async (
  mainId,
  userId,
  companyId,
  fieldArrayHandlers = {}
) => {
  // Respect edit/loading guards
  try {
    if (fieldArrayHandlers && fieldArrayHandlers.calcId) return;
    if (
      fieldArrayHandlers &&
      fieldArrayHandlers.isPopulatingRef &&
      fieldArrayHandlers.isPopulatingRef.current
    )
      return;
  } catch (e) {
    /* ignore */
  }

  // mark populating so other effects skip heavy work
  try {
    if (fieldArrayHandlers && fieldArrayHandlers.isPopulatingRef)
      fieldArrayHandlers.isPopulatingRef.current = true;
  } catch (e) {
    /* ignore */
  }

  try {
    if (!mainId || !userId || !companyId) return;
    const data = await getUserTemplate(companyId);

    if (!data) return;

    const parseMaybeJson = (v) => {
      if (v == null) return null;
      if (typeof v === "string") {
        try {
          return JSON.parse(v);
        } catch (e) {
          return null;
        }
      }
      return Array.isArray(v) ? v : null;
    };

    const toCamel = (s = "") =>
      s
        .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
        .replace(/^([A-Z])/, (m) => m.toLowerCase());

    const parseTemplateValue = (item) => {
      if (!item) return "0.00";
      let v = item?.value ?? item?.rate ?? item?.Cash ?? null;
      if (v == null) return "0.00";
      if (typeof v === "string") {
        const s = v.trim().replace(/,/g, "");
        if (s === "") return "0.00";
        return s;
      }
      return v;
    };

    // parsedSection2 (with camelKey and parsed value)
    const rawSection2 =
      parseMaybeJson(data?.seller_closing_costs_section2) || [];
    const parsedSection2 = [];
    if (Array.isArray(rawSection2)) {
      for (const it of rawSection2) {
        if (!it || !it.key) continue;
        const value = parseTemplateValue(it);
        const camel = toCamel(it.key);
        parsedSection2.push({ ...it, value, camelKey: camel });
      }
    }

    // parsedSection1 with IGNORE_KEYS
    const rawSection1 =
      parseMaybeJson(data?.seller_closing_costs_section1) || [];
    const parsedSection1 = [];
    if (Array.isArray(rawSection1)) {
      const IGNORE_KEYS = [
        "reconveyance_fee",
        "payoff_demand_statement",
        "prepayment_penality",
      ];
      for (const it of rawSection1) {
        if (!it || !it.key) continue;
        if (IGNORE_KEYS.includes((it.key || "").toString())) continue;
        const value = parseTemplateValue(it);
        const camel = toCamel(it.key);
        const key = (it.key || "").toString();
        if (key === "transfer_tax") {
          parsedSection1.push({ ...it, value, camelKey: camel, amount: 0 });
        } else {
          parsedSection1.push({ ...it, value, camelKey: camel });
        }
      }
    }

    // sellerOtherCosts parsing and loan-type variants
    const rawSellerOther =
      parseMaybeJson(data?.conventional_seller_closing_costs_section) || [];
    const parsedSellerOtherCosts = [];
    if (Array.isArray(rawSellerOther)) {
      const IGNORE_KEYS = new Set([
        "points_pct",
        "origination_factor_pct",
        "origination_factor",
      ]);
      for (const it of rawSellerOther) {
        if (!it || !it.key) continue;
        if (IGNORE_KEYS.has((it.key || "").toString())) continue;
        const value = parseTemplateValue(0);
        const camel = toCamel(it.key);
        parsedSellerOtherCosts.push({ ...it, value, camelKey: camel });
      }
    }

    const parseOtherCostsArray = (arr) => {
      const out = [];
      if (!Array.isArray(arr)) return out;
      const IGNORE_KEYS_LOCAL = new Set([
        "points_pct",
        "origination_factor_pct",
        "origination_factor",
      ]);
      for (const it of arr) {
        if (!it || !it.key) continue;
        if (IGNORE_KEYS_LOCAL.has((it.key || "").toString())) continue;
        let value = parseTemplateValue(it);
        try {
          const payorKey = Object.keys(it).find((k) =>
            (k || "").toString().toLowerCase().includes("payer")
          );
          if (payorKey) {
            const payorVal = (it[payorKey] ?? "")
              .toString()
              .trim()
              .toLowerCase();
            if (payorVal && payorVal !== "seller") {
              value = "0.00";
            }
          }
        } catch (e) {
          /* ignore */
        }
        const camel = toCamel(it.key);
        out.push({ ...it, value, camelKey: camel });
      }
      return out;
    };

    const fhaOther = parseOtherCostsArray(
      data?.fha_closing_costs ?? data?.fha_closing_costs_section ?? []
    );
    const usdaOther = parseOtherCostsArray(
      data?.usda_closing_costs ?? data?.usda_closing_costs_section ?? []
    );
    const vaOther = parseOtherCostsArray(
      data?.va_closing_costs ?? data?.va_closing_costs_section ?? []
    );

    // include corrective_work and buyers_fees from seller_othercosts.other_costs into sellerOtherCostMeta
    try {
      let extraOtherCosts = data?.seller_othercosts?.other_costs ?? null;
      if (typeof extraOtherCosts === "string") {
        try {
          extraOtherCosts = JSON.parse(extraOtherCosts);
        } catch (e) {
          extraOtherCosts = null;
        }
      }
      if (Array.isArray(extraOtherCosts)) {
        const EXTRA_KEYS = new Set(["corrective_work", "buyers_fees"]);
        for (const it of extraOtherCosts) {
          if (!it || !it.key) continue;
          const keyNorm = (it.key || "").toString();
          if (!EXTRA_KEYS.has(keyNorm)) continue;
          const already = parsedSellerOtherCosts.find(
            (x) => (x.key || "").toString() === keyNorm
          );
          if (already) continue;
          const value = parseTemplateValue(it);
          const camel = toCamel(it.key);
          const defaultShape = {
            unit: it.unit ?? "$",
            label:
              it.label ??
              (it.key === "corrective_work"
                ? "Corrective Work"
                : it.key === "buyers_fees"
                ? "Buyer's Fees"
                : it.key),
            sr_no:
              it.sr_no ??
              (it.key === "corrective_work"
                ? 7
                : it.key === "buyers_fees"
                ? 8
                : undefined),
          };
          parsedSellerOtherCosts.push({
            ...defaultShape,
            ...it,
            value,
            camelKey: camel,
          });
        }
      }
    } catch (e) {
      /* non-critical */
    }

    const templateSellerOtherCosts = {
      conventional: parsedSellerOtherCosts,
      fha: fhaOther,
      usda: usdaOther,
      va: vaOther,
    };

    // otherCostMeta: skip corrective_work and buyers_fees
    const rawOtherCostMeta =
      parseMaybeJson(data?.seller_othercosts?.other_costs) || [];
    const parsedOtherCosts = [];
    if (Array.isArray(rawOtherCostMeta)) {
      const SKIP_KEYS = new Set(["corrective_work", "buyers_fees"]);
      for (const it of rawOtherCostMeta) {
        if (!it || !it.key) continue;
        if (SKIP_KEYS.has((it.key || "").toString())) continue;
        const value = parseTemplateValue(it);
        const camel = toCamel(it.key);
        const amount = parseTemplateValue(it);
        parsedOtherCosts.push({ ...it, value, camelKey: camel, amount });
      }
    }

    // compensation: skip days_interest
    const rawComp =
      parseMaybeJson(data?.seller_othercosts?.payments_to_buyer) || [];
    const parsedPaymentToBuyer = [];
    if (Array.isArray(rawComp)) {
      const SKIP_PAYMENT_KEYS = new Set(["days_interest"]);
      for (const it of rawComp) {
        if (!it || !it.key) continue;
        if (SKIP_PAYMENT_KEYS.has((it.key || "").toString())) continue;
        const value = parseTemplateValue(it);
        const camel = toCamel(it.key);
        parsedPaymentToBuyer.push({ ...it, value, camelKey: camel });
      }
    }

    let loanInfoMeta = null;
    loanInfoMeta = {
      annualPropertyTax: "0.00",
      assessorsTaxRate: "0.00",
      autoEstimateLoan: false,
      buyersLoanType: "Conventional",
      buyerDownPayment: "0.00",
      estimatedTaxProrations: "0.00",
      loansToBePaidBalance1: "0.00",
      loansToBePaidRate1: "0.00",
      loansToBePaidBalance2: "0.00",
      loansToBePaidRate2: "0.00",
      loansToBePaidBalance3: "0.00",
      loansToBePaidRate3: "0.00",
      transferTaxRatePer: "0.00",
      homeWarrantyAmount: "0.00",
      estimatedClosingDate: "",
      mortgageAmount: "0.00",
      conventionalPercent: "80.00",
      conventionalPercent2: "0.00",
      interestRate1: "0.00",
      interestRate2: "0.00",
      termsInYears1: "0",
      termsInYears2: "0",
      amount: "0.00",
      amount2: "0.00",
      adjusted: "0.00",
      noPMICheck: false,
      showAPRCheck: false,
      allowOwnerCarry1Check: false,
      allowOwnerCarry2Check: false,
      addDueYear1: "0.00",
      addDueYear2: "0.00",
    };

    const handlers = fieldArrayHandlers || {};
    try {
      if (
        Array.isArray(parsedSection2) &&
        typeof handlers.replaceClosingCostsSection2 === "function"
      )
        handlers.replaceClosingCostsSection2(parsedSection2);
      if (
        Array.isArray(parsedSection1) &&
        typeof handlers.replaceSellerClosingCost === "function"
      )
        handlers.replaceSellerClosingCost(parsedSection1);
      if (
        Array.isArray(parsedSellerOtherCosts) &&
        typeof handlers.replaceSellerOtherCost === "function"
      )
        handlers.replaceSellerOtherCost(parsedSellerOtherCosts);
      if (
        Array.isArray(parsedOtherCosts) &&
        typeof handlers.replaceOtherCost === "function"
      )
        handlers.replaceOtherCost(parsedOtherCosts);
      if (
        Array.isArray(parsedPaymentToBuyer) &&
        typeof handlers.replaceCompensation === "function"
      )
        handlers.replaceCompensation(parsedPaymentToBuyer);
    } catch (e) {
      console.warn("loadUserTemplate: failed to replace some arrays", e);
    }

    try {
      const setRHF =
        typeof handlers.setValue === "function" ? handlers.setValue : null;
      const getRHF =
        typeof handlers.getValues === "function" ? handlers.getValues : null;
      if (setRHF) {
        try {
          setRHF("closingCostMeta", parsedSection2);
        } catch (e) {}
        try {
          setRHF("sellerClosingCostMeta", parsedSection1);
        } catch (e) {}
        try {
          setRHF("sellerOtherCostMeta", parsedSellerOtherCosts);
        } catch (e) {}
        try {
          setRHF("compensationMeta", parsedPaymentToBuyer);
        } catch (e) {}
        try {
          setRHF("otherCostMeta", parsedOtherCosts);
        } catch (e) {}
        try {
          setRHF("templateSellerOtherCosts", templateSellerOtherCosts);
        } catch (e) {}
        try {
          setRHF("preparedBy", "");
        } catch (e) {}
        try {
          setRHF("preparedFor", "New Client");
        } catch (e) {}
        try {
          setRHF("salesPrice", "0.00");
        } catch (e) {}

        // Only set loanInfoMeta if caller doesn't already have it populated
        try {
          const existingLoanInfo = getRHF ? getRHF("loanInfoMeta") ?? {} : null;
          const hasExistingLoanInfo =
            existingLoanInfo &&
            Object.keys(existingLoanInfo || {}).some((k) => {
              const v = existingLoanInfo[k];
              return v !== null && v !== undefined && String(v).trim() !== "";
            });
          if (!hasExistingLoanInfo) {
            try {
              setRHF("loanInfoMeta", loanInfoMeta);
            } catch (e) {}
          }
        } catch (e) {
          try {
            setRHF("loanInfoMeta", loanInfoMeta);
          } catch (err) {
            /* ignore */
          }
        }
      }
    } catch (e) {
      /* ignore */
    }
  } catch (err) {
    console.error("loadUserTemplate error", err);
  } finally {
    try {
      if (fieldArrayHandlers && fieldArrayHandlers.isPopulatingRef)
        fieldArrayHandlers.isPopulatingRef.current = false;
    } catch (e) {
      /* ignore */
    }
  }
};
